import { useState, useEffect, useCallback } from 'react';
import apiClient from '../utils/api';

export interface FixedTagStatItem {
  category_id: string;
  category_name: string;
  option_id: string;
  option_name: string;
  count: number;
  display_order: number;
  cat_order: number;
}

export interface FixedTagCategoryStats {
  category_id: string;
  category_name: string;
  options: {
    option_id: string;
    option_name: string;
    count: number;
  }[];
}

export interface UseFixedTagStatsProps {
  autoRefresh?: boolean;
  onError?: (error: string) => void;
}

export const useFixedTagStats = ({
  autoRefresh = false,
  onError,
}: UseFixedTagStatsProps = {}) => {
  const [categories, setCategories] = useState<FixedTagCategoryStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = useCallback(async () => {
    try {
      setError(null);
      const response = await apiClient.getFixedTagStats();

      if (response.success && response.stats) {
        const catMap = new Map<string, FixedTagCategoryStats>();

        for (const row of response.stats) {
          if (!catMap.has(row.category_id)) {
            catMap.set(row.category_id, {
              category_id: row.category_id,
              category_name: row.category_name,
              options: [],
            });
          }
          catMap.get(row.category_id)!.options.push({
            option_id: row.option_id,
            option_name: row.option_name,
            count: row.count,
          });
        }

        setCategories(Array.from(catMap.values()));
      }
    } catch (err: any) {
      if (!err.response || err.response.status !== 401) {
        const msg = err instanceof Error ? err.message : '固定タグ統計の取得に失敗しました';
        setError(msg);
        onError?.(msg);
      }
    } finally {
      setLoading(false);
    }
  }, [onError]);

  useEffect(() => {
    fetchStats();
  }, []);

  const refresh = useCallback(() => {
    setLoading(true);
    fetchStats();
  }, [fetchStats]);

  return { categories, loading, error, refresh };
};

export default useFixedTagStats;
